import './index.scss';

import React from 'react';
import NumberFormat from 'react-number-format';
import Scrollbar from 'react-scrollbars-custom';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
import { format as formatDate } from 'date-fns';
import { connect } from 'react-redux';
import { Layout, Button, Form, Input, Row, Col, Result, message } from 'antd';
import { LeftOutlined, LoadingOutlined, CloseCircleFilled } from '@ant-design/icons';
import getNested from '../../utils/getNested';
import { getBookingDuration } from '../../utils/bookingUtil';
import { bookingActions } from '../../actions/booking';
import { BookingStep } from '../../constants';
import { User } from '../../types/user';
import { Service } from '../../types/service';
import { Configuration } from '../../types/configuration';
import { StylistType } from '../../types/stylist';
import stripeService from '../../services/stripeService';
import BookingSummary from '../../components/BookingSummary';
import BookingDetailHeader, { LayoutType } from '../../components/BookingDetailHeader';

const { Password, TextArea } = Input;

enum BookingSubmitStatus {
  HasInvalidCredentials = -1,
  HasExistingAlready = -2,
  PaymentError = 1,
}

interface Props {
  users: User[];
  activeUserId: number;
  isAdmin: boolean;
  salonId: number;
  bookingId: number;
  currentBooking: any;
  accessToken: string;
  redirectUrl: string;
  bookingSettings: Configuration;

  bookingNotes: string;
  storeBookingNotes: (notes: string) => void;
  creditCard: any;
  storeCreditCard: (card: any) => void;
  updateStep: (step: number) => void;
  createBooking: (data: any) => Promise<any>;
  updateBooking: (data: any, accessToken: string) => Promise<any>;
}

interface States {
  isMobile: boolean;
  isSubmitting?: boolean;
  isSubmitSuccess?: boolean;
  submitError?: string;
  submitErrorLink?: string;
}

class BookingConfirmation extends React.PureComponent<Props, States> {
  formRef;
  state;

  constructor(props) {
    super(props);
    this.state = { isMobile: true };
    this.formRef = React.createRef();
  }

  onWindowResize() {
    const { innerWidth } = window;
    this.setState({isMobile: innerWidth < 576 });
  }

  componentDidMount() {
    this.onWindowResize();
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  componentWillUnmount() {
    this.setState({
      isSubmitting: false,
      isSubmitSuccess: false,
      submitError: undefined,
      submitErrorLink: undefined,
    });
    window.removeEventListener('resize', this.onWindowResize.bind(this));
  }

  onChangeNotes = debounce((notes: string) => {
    const { storeBookingNotes } = this.props;
    storeBookingNotes(notes);
  }, 500);

  onChangeCreditCard = debounce((newUpdates) => {
    const { creditCard, storeCreditCard } = this.props;
    storeCreditCard({ ...creditCard, ...newUpdates });
  }, 500);

  onBookingCompleted = debounce(() => {
    const { redirectUrl } = this.props;
    if (redirectUrl) {
      this.redirect(redirectUrl);
    }
  }, 3000);

  private redirect(url: string) {
    const targetWindow = window.parent || window;
    if (targetWindow) {
      targetWindow.location.href = url;
    }
  }

  private createStripeToken() {
    const { creditCard } = this.props;
    const { cardName, cardNumber, cardExpiry, cardCCV } = creditCard;

    if (!!cardName && !!cardNumber && !!cardExpiry && !!cardCCV) {
      const [expiryMonth, expiryYear] = cardExpiry.split('/');
      return stripeService.createCardToken({
        name: cardName,
        number: Number(String(cardNumber).split(' ').join('')),
        exp_month: Number(expiryMonth),
        exp_year: Number(expiryYear),
        cvc: Number(cardCCV),
      });
    }

    return Promise.resolve({});
  }

  private formatExpiryMonth(value, max) {
    let expiryMonth = value;

    if (expiryMonth.length === 1 && expiryMonth[0] > max[0]) {
      expiryMonth = `0${expiryMonth}`;
    }

    if (expiryMonth.length === 2) {
      if (Number(expiryMonth) === 0) {
        expiryMonth = '01';
        // this can happen when user paste number
      } else if (expiryMonth > max) {
        expiryMonth = max;
      }
    }

    return expiryMonth;
  }

  private cardExpiry(value) {
    const expiryString = String(value);
    const month = this.formatExpiryMonth(expiryString.substring(0, 2), 12);
    const year = expiryString.substring(2, 4);

    return `${month}${year.length ? `/${year}` : ''}`;
  }

  private formatGroupStylists(user: User, isNoPreference = false) {
    const services = getNested(user, 'selectedServices', []) || [];

    if (services.length > 0) {
      if (isNoPreference) {
        return [{
          stylist_id: StylistType.NoPreference,
          services: services.map(service => service.id),
        }];
      }

      const stylistIds = uniq(services.map((service: Service) => service.stylistId));

      if (stylistIds.length === 1) {
        return [{
          stylist_id: stylistIds[0],
          services: services.map((service: Service) => service.id),
        }];
      }

      return services.map((service: Service) => ({
        stylist_id: service.stylistId,
        services: [service.id],
      }));
    }

    return [];
  }

  private formatGroupUsers(user, isNoPreference = false) {
    return {
      first_name: user.firstName,
      last_name: user.lastName,
      phone: user.phoneNumber,
      group_stylist: this.formatGroupStylists(user, isNoPreference),
    };
  }

  private onCreateBookingSucceed(response) {
    const {
      status,
      info,
      booking_link: submitErrorLink,
    } = (response.data || {}) as any;
    if (status !== true && status !== 0) {
      let submitError = info || 'Something went wrong when storing your appointment. Please try again or contact salon for assistance.';

      if (status === BookingSubmitStatus.HasInvalidCredentials) {
        submitError = 'Phone number cannot be reached. Please check your phone number or contact salon for assistance.';
      } else if (status === BookingSubmitStatus.HasExistingAlready) {
        submitError = 'You have already made an appointment this time. If you would like to make change please contact salon for assistance.';
      }

      this.setState({ submitError, submitErrorLink });

      // display error message
      setTimeout(() => this.showMessage(), 300);
    } else {
      this.setState({ isSubmitSuccess: true });

      // auto redirect to a destination page after 3s
      this.onBookingCompleted();
    }
  }

  private onCreateBookingFailed(response) {
    const submitError = getNested(response, 'error.message', null)
      || 'Something went wrong when storing your appointment. Please try again or contact salon for assistance.';
    this.setState({ submitError });

    // display error message
    setTimeout(() => this.showMessage(), 300);
  }

  private showMessage() {
    const { submitError, submitErrorLink } = this.state;

    if (!!submitError) {
      message.error({
        content: <div style={{ display: 'flex', flexDirection: 'row' }}>
          <CloseCircleFilled />

          <div style={{
            textAlign: 'left',
            flexShrink: 1,
            flexGrow: 1,
          }}>
            {submitError} {submitErrorLink && (
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => this.redirect(submitErrorLink)}
              >View it?</span>
            )}
          </div>
        </div>,
        icon: <span />,
        duration: 3,
      });
    }
  }

  private onCreate(options: any) {
    const { createBooking, updateBooking } = this.props;
    const { bookingId, bookingData, accessToken } = options;

    if (bookingId) {
      return updateBooking(bookingData, accessToken);
    }

    return createBooking(bookingData);
  };

  submitBooking(values) {
    const { users, accessToken, salonId, bookingNotes, bookingId, currentBooking } = this.props;
    const isEditing = !!bookingId && !!currentBooking;
    const { firstName, lastName, phoneNumber, selectedDate, selectedTime } = users[0] || {};
    const bookingDate = formatDate(selectedDate, 'MM-dd-yyyy');
    const bookingTime = String(selectedTime).replace(' ', '');

    const { cardDetail } = values;
    const cardData = cardDetail.id
      ? { source: cardDetail.id, billing_zipcode: getNested(cardDetail, 'card.address_zip', null) }
      : {};
    const bookingData = {
      ...cardData,
      user_id: -1,
      salon_id: salonId,
      booking_date_time: `${bookingDate} ${bookingTime}`,
      special_attention: bookingNotes,
      email_or_phone: 2,
      first_name: firstName,
      last_name: lastName,
      email_phone: phoneNumber,
      duration: getBookingDuration(users),
      group_booking: users.map(user => this.formatGroupUsers(user)),
    };

    let updateBookingData = { bookingData } as any;

    if (isEditing) {
      const updatedData = {
        salon_id: bookingData.salon_id,
        edit_booking_id: currentBooking.id,
        booking_date_time: bookingData.booking_date_time,
        special_attention: bookingData.special_attention,
        group_booking: bookingData.group_booking,
      };

      updateBookingData = {
        bookingId: currentBooking.id,
        bookingData: updatedData,
        accessToken,
      };
    }

    return this.onCreate(updateBookingData)
      .then(response => this.onCreateBookingSucceed(response))
      .catch(error => this.onCreateBookingFailed(error))
      ;
  }

  async onSubmit(values) {
    this.setState({ isSubmitting: true });

    try {
      const cardDetail = await this.createStripeToken();
      await this.submitBooking({ ...values, cardDetail });
    } catch(response) {
      this.onCreateBookingFailed(response);
    }

    this.setState({ isSubmitting: false });
  }

  renderForm() {
    const { isAdmin, bookingId, currentBooking, bookingSettings } = this.props;
    const { canPayByCreditCard, mustPayByCreditCard, depositAmount, paymentTerms } = bookingSettings;
    const isEditing = !!bookingId && !!currentBooking;
    const isPaymentRequired = !isAdmin && mustPayByCreditCard;

    return (
      <div
        className="booking-confirmation--summary--content"
        style={{ padding: 16, marginBottom: 24, background: '#ffffff' }}
      >
        {/* {submitError && (
          <Row gutter={24}>
            <Col xs={24} md={18} lg={12}>
              <Alert
                type="error"
                message={<div>
                  {submitError} {submitErrorLink && (
                    <span
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => this.redirect(submitErrorLink)}
                    >View it?</span>
                  )}
                </div>}
                style={{ marginBottom: 20 }}
              />
            </Col>
          </Row>
        )} */}

        <div className="booking-confirmation--notes">
          <Row gutter={24}>
            <Col xs={24} md={18} lg={12}>
              <div
                className="booking-confirmation--notes-label"
                style={{ marginBottom: 6, fontWeight: 600 }}
              >Notes</div>
              <Form.Item name="bookingNotes">
                <TextArea
                  rows={4}
                  placeholder="Leave a note for your booking"
                  onChange={({ target: { value } }) => this.onChangeNotes(value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {!isEditing && (canPayByCreditCard || mustPayByCreditCard) && (
          <div className="booking-confirmation--payment">
            <div
              className="booking-confirmation--payment-heading"
              style={{ marginBottom: 6, fontWeight: 600 }}
            >Payment detail</div>

            <Row gutter={24}>
              <Col xs={24} md={18} lg={12}>
                <Form.Item
                  name="cardNumber"
                  rules={[{
                    required: isPaymentRequired,
                    message: 'This field is required'
                  }]}
                >
                  <NumberFormat
                    customInput={Input}
                    format="#### #### #### ####"
                    placeholder="Card number"
                    onChange={({ target: { value } }) => this.onChangeCreditCard({ cardNumber: value })}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={12} md={9} lg={6}>
                <Form.Item
                  name="cardExpiry"
                  rules={[{
                    required: isPaymentRequired,
                    message: 'This field is required'
                  }]}
                >
                  <NumberFormat
                    format={value => this.cardExpiry(value)}
                    placeholder="Expiry date"
                    customInput={Input}
                    onChange={({ target: { value } }) => this.onChangeCreditCard({ cardExpiry: value })}
                  />
                </Form.Item>
              </Col>

              <Col xs={12} md={9} lg={6}>
                <Form.Item
                  name="cardCCV"
                  rules={[{
                    required: isPaymentRequired,
                    message: 'This field is required'
                  }]}
                >
                  <Password
                    placeholder="CCV"
                    onChange={({ target: { value } }) => this.onChangeCreditCard({ cardCCV: value })}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={18} lg={12}>
                <Form.Item
                  name="cardName"
                  rules={[{
                    required: isPaymentRequired,
                    message: 'This field is required'
                  }]}
                >
                  <Input
                    placeholder="Name on card"
                    onChange={({ target: { value } }) => this.onChangeCreditCard({ cardName: value })}
                  />
                </Form.Item>
              </Col>
            </Row>

            {depositAmount > 0 && (
              <>
                <div
                  className="booking-confirmation--payment-heading"
                  style={{ marginBottom: 6, fontWeight: 600 }}
                >Deposit amount ($)</div>
                <Row gutter={24}>
                  <Col xs={24} md={18} lg={12}>
                    <div style={{ marginBottom: 24 }}>{depositAmount}</div>
                  </Col>
                </Row>
              </>
            )}

            {paymentTerms.length > 0 && (
              <>
                <div
                  className="booking-confirmation--payment-heading"
                  style={{ marginBottom: 6, fontWeight: 600 }}
                >Payment term</div>
                <Row gutter={24}>
                  <Col xs={24} md={18} lg={12}>
                    <div style={{ marginBottom: 24 }}>
                      {paymentTerms.map(term => (
                        <p key={`term-${Math.random()}`}>{term}</p>
                      ))}
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  renderMobileContent() {
    return (
      <>
        <div className="booking-confirmation--summary--header">
          <BookingDetailHeader layout={LayoutType.List} />
        </div>

        <Layout
          className="fave-layout fave-block"
          style={{
            position: 'relative',
            padding: 16,
          }}
        >
          <Layout.Header className="fave-block--header fave-block--header-mobile">
            <b>Confirmation</b>
          </Layout.Header>

          <Layout.Content
            className="fave-block--content fave-block--content-mobile"
            style={{ padding: 0 }}
          >
            {this.renderForm()}
          </Layout.Content>
        </Layout>
      </>
    );
  }

  renderDesktopContent() {
    return (
      <Layout
        className="fave-layout fave-block booking-detail--content-container-desktop fave-block--booking-confirmation"
        style={{
          border: '1px solid #f0f0f0',
          height: 'auto !important',
          width: 'auto !important',
          bottom: 24,
          right: 16,
          left: 16,
          top: 16,
        }}
      >
        <Layout.Header className="fave-block--header fave-block--header-desktop">
          Confirmation
        </Layout.Header>

        <Layout.Content
          className="fave-block--content fave-block--content-desktop"
          style={{ padding: 0 }}
        >
          <Scrollbar>
            <div className="fave-block--booking-confirmation--content">
              {this.renderForm()}
            </div>
          </Scrollbar>
        </Layout.Content>
      </Layout>
    );
  }

  renderFooter() {
    const { isSubmitting } = this.state;
    const { bookingId, currentBooking } = this.props;
    const isEditing = !!bookingId && !!currentBooking;

    return (
      <>
        <Button
          size="large"
          className="btn-back"
          icon={<LeftOutlined />}
          disabled={isSubmitting}
          onClick={() => this.gotoBookingDetail()}
        >Back</Button>

        <Button
          size="large"
          type="primary"
          htmlType="submit"
          style={isSubmitting ? { opacity: 0.65, pointerEvents: 'none' } : {}}
        >
          {isEditing ? 'Update now' : 'Book now'} {isSubmitting && <LoadingOutlined style={{ marginLeft: 8 }} />}
        </Button>
      </>
    );
  }

  gotoBookingDetail() {
    const { updateStep } = this.props;
    updateStep(BookingStep.BookingDetail);
  }

  render() {
    const { isMobile, isSubmitSuccess } = this.state;
    const { bookingNotes, creditCard, bookingId, currentBooking } = this.props;
    const isEditing = !!bookingId && !!currentBooking;

    return (
      <Form
        ref={this.formRef}
        initialValues={{
          ...creditCard,
          bookingNotes,
        }}
        onFinish={values => this.onSubmit(values)}
      >
        {!isSubmitSuccess && (
          <Layout className="fave-layout booking-detail booking-confirmation">
            <Layout.Content
              className="fave-layout--content"
              style={{ position: 'relative', padding: 0 }}
            >
              <Layout className="fave-layout booking-detail--content">
                <Layout.Content
                  className="fave-layout--content"
                  style={{ padding: 0, position: "relative" }}
                >
                  {isMobile ? this.renderMobileContent() : this.renderDesktopContent()}
                </Layout.Content>

                <Layout.Sider className="fave-layout--slider">
                  <div className="booking-detail--content-inner">
                    <BookingSummary />
                  </div>
                </Layout.Sider>
              </Layout>
            </Layout.Content>

            <Layout.Footer className="fave-layout--footer">
              <div className="fave-layout--footer-actions">
                {this.renderFooter()}
              </div>
            </Layout.Footer>
          </Layout>
        )}

        {isSubmitSuccess && (
          <div className="booking-confirmation" style={{ padding: 24 }}>
            <Result
              status="success"
              title="Confirmed!"
              subTitle={`Your appointment has been ${isEditing ? 'updated' : 'booked'} successfully`}
            />
          </div>
        )}
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  users: state.bookingStore.users,
  activeUserId: state.bookingStore.activeUserId,
  bookingNotes: state.bookingStore.bookingNotes,
  creditCard: state.bookingStore.creditCard,
  isAdmin: state.salonStore.isAdmin,
  salonId: state.salonStore.salonId,
  bookingId: state.salonStore.bookingId,
  currentBooking: state.bookingStore.currentBooking,
  accessToken: state.salonStore.accessToken,
  redirectUrl: state.salonStore.redirectUrl,
  bookingSettings: state.bookingSettings,
});
const mapDispatchToProps = dispatch => ({
  updateStep: (step: number) => dispatch(bookingActions.updateStep(step)),
  storeBookingNotes: (notes: string) => dispatch(bookingActions.storeBookingNotes(notes)),
  storeCreditCard: (creditCard) => dispatch(bookingActions.storeCreditCard(creditCard)),
  createBooking: (data) => dispatch(bookingActions.createBooking(data)),
  updateBooking: (data: any, accessToken: string) => dispatch(bookingActions.updateBooking(data, accessToken)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BookingConfirmation);
